import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { loadsAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { format } from 'date-fns';
import { MapPin, Calendar as CalendarIcon, DollarSign, Truck, Package, Loader2, ArrowLeft, Car, Zap, Box, AlertTriangle, Scale } from 'lucide-react';
import { toast } from 'sonner';

const PostLoadPage = () => {
  const navigate = useNavigate();
  const { canPostLoads } = useAuth();
  const [loading, setLoading] = useState(false);
  const [pickupDate, setPickupDate] = useState(null);
  const [deliveryDate, setDeliveryDate] = useState(null);
  const [formData, setFormData] = useState({
    load_category: 'auto_transport',
    vehicle_type: '',
    pickup_location: '',
    delivery_location: '',
    price: '',
    mileage: '',  // Total miles for the route
    // Auto Transport fields
    vehicle_count: '1',
    vehicle_make_model: '',
    vehicle_year: '',
    vehicle_notes: '',
    is_running: true,
    is_enclosed: false,
    // Hotshot/Freight shared fields
    weight: '',
    dimensions_length: '',
    dimensions_width: '',
    dimensions_height: '',
    is_hazmat: false,
    special_instructions: '',
    // Hotshot specific
    is_time_sensitive: false,
    trailer_type: '',
    // Freight specific
    freight_class: '',
    pallet_count: '',
    is_ltl: false,
  });

  const loadCategories = [
    { value: 'auto_transport', label: 'Auto Transport', icon: Car, color: 'text-blue-400' },
    { value: 'hotshot', label: 'Hotshot', icon: Zap, color: 'text-yellow-400' },
    { value: 'freight', label: 'Freight', icon: Box, color: 'text-green-400' },
  ];

  const vehicleTypes = {
    auto_transport: [
      { value: 'open_carrier', label: 'Open Carrier' },
      { value: 'enclosed_carrier', label: 'Enclosed Carrier' },
      { value: 'driveaway', label: 'Driveaway' },
      { value: 'single_vehicle', label: 'Single Vehicle' },
      { value: 'multi_car', label: 'Multi-Car' },
      { value: 'oversized_vehicle', label: 'Oversized Vehicle' },
    ],
    hotshot: [
      { value: 'hotshot', label: 'Hotshot Trailer' },
      { value: 'flatbed', label: 'Flatbed' },
    ],
    freight: [
      { value: 'flatbed', label: 'Flatbed' },
      { value: 'dry_van', label: 'Dry Van' },
      { value: 'reefer', label: 'Reefer (Refrigerated)' },
    ],
  };

  const trailerTypes = [
    { value: 'flatbed', label: 'Flatbed' },
    { value: 'gooseneck', label: 'Gooseneck' },
    { value: 'lowboy', label: 'Lowboy' },
    { value: 'step_deck', label: 'Step Deck' },
    { value: 'rgn', label: 'RGN (Removable Gooseneck)' },
  ];

  const freightClasses = [
    { value: '50', label: 'Class 50 (Clean Freight)' },
    { value: '55', label: 'Class 55' },
    { value: '60', label: 'Class 60' },
    { value: '65', label: 'Class 65' },
    { value: '70', label: 'Class 70' },
    { value: '77.5', label: 'Class 77.5' },
    { value: '85', label: 'Class 85' },
    { value: '92.5', label: 'Class 92.5' },
    { value: '100', label: 'Class 100' },
    { value: '110', label: 'Class 110' },
    { value: '125', label: 'Class 125' },
    { value: '150', label: 'Class 150' },
    { value: '175', label: 'Class 175' },
    { value: '200', label: 'Class 200' },
    { value: '250', label: 'Class 250' },
    { value: '300', label: 'Class 300' },
    { value: '400', label: 'Class 400' },
    { value: '500', label: 'Class 500 (Low Density)' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!pickupDate) {
      toast.error('Please select a pickup date');
      return;
    }

    if (!formData.vehicle_type) {
      toast.error('Please select a vehicle type');
      return;
    }

    setLoading(true);
    try {
      const loadData = {
        load_category: formData.load_category,
        vehicle_type: formData.vehicle_type,
        pickup_location: formData.pickup_location,
        delivery_location: formData.delivery_location,
        price: parseFloat(formData.price),
        mileage: formData.mileage ? parseFloat(formData.mileage) : null,
        pickup_date: pickupDate.toISOString(),
        delivery_date: deliveryDate ? deliveryDate.toISOString() : null,
        special_instructions: formData.special_instructions || null,
        is_hazmat: formData.is_hazmat,
      };

      // Add category-specific fields
      if (formData.load_category === 'auto_transport') {
        loadData.vehicle_count = parseInt(formData.vehicle_count) || 1;
        loadData.vehicle_make_model = formData.vehicle_make_model || null;
        loadData.vehicle_year = formData.vehicle_year || null;
        loadData.vehicle_notes = formData.vehicle_notes || null;
        loadData.is_running = formData.is_running;
        loadData.is_enclosed = formData.is_enclosed;
      } else if (formData.load_category === 'hotshot') {
        loadData.weight = formData.weight ? parseFloat(formData.weight) : null;
        loadData.dimensions_length = formData.dimensions_length ? parseFloat(formData.dimensions_length) : null;
        loadData.dimensions_width = formData.dimensions_width ? parseFloat(formData.dimensions_width) : null;
        loadData.dimensions_height = formData.dimensions_height ? parseFloat(formData.dimensions_height) : null;
        loadData.is_time_sensitive = formData.is_time_sensitive;
        loadData.trailer_type = formData.trailer_type || null;
      } else if (formData.load_category === 'freight') {
        loadData.weight = formData.weight ? parseFloat(formData.weight) : null;
        loadData.dimensions_length = formData.dimensions_length ? parseFloat(formData.dimensions_length) : null;
        loadData.dimensions_width = formData.dimensions_width ? parseFloat(formData.dimensions_width) : null;
        loadData.dimensions_height = formData.dimensions_height ? parseFloat(formData.dimensions_height) : null;
        loadData.freight_class = formData.freight_class || null;
        loadData.pallet_count = formData.pallet_count ? parseInt(formData.pallet_count) : null;
        loadData.is_ltl = formData.is_ltl;
      }

      const response = await loadsAPI.create(loadData);
      toast.success('Load posted successfully!');
      navigate(`/loads/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post load');
    } finally {
      setLoading(false);
    }
  };

  if (!canPostLoads) {
    return (
      <div className="min-h-screen bg-background pt-20 flex items-center justify-center px-4">
        <Card className="max-w-md w-full border-white/5 bg-card/50">
          <CardContent className="pt-6 text-center">
            <Package className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-xl font-bold mb-2">Access Restricted</h2>
            <p className="text-muted-foreground mb-4">
              Only brokers, shippers, dealers, auctions, and fleet owners can post loads.
            </p>
            <Button onClick={() => navigate('/loadboard')}>
              Browse Loads Instead
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-20 pb-12 px-4">
      <div className="max-w-2xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <Card className="border-white/5 bg-card/50 animate-fade-in">
          <CardHeader>
            <CardTitle className="text-2xl font-bold uppercase font-['Barlow_Condensed'] tracking-wide">
              Post a Load
            </CardTitle>
            <CardDescription>
              Create a new transport listing for carriers to view
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Category & Type */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Load Category</Label>
                  <Select
                    value={formData.load_category}
                    onValueChange={(value) => setFormData({ ...formData, load_category: value, vehicle_type: '' })}
                  >
                    <SelectTrigger className="h-12 bg-secondary/30 border-white/10" data-testid="load-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {loadCategories.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Vehicle/Equipment Type</Label>
                  <Select
                    value={formData.vehicle_type}
                    onValueChange={(value) => setFormData({ ...formData, vehicle_type: value })}
                  >
                    <SelectTrigger className="h-12 bg-secondary/30 border-white/10" data-testid="vehicle-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {vehicleTypes[formData.load_category]?.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Locations */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="pickup_location">Pickup Location</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-400" />
                    <Input
                      id="pickup_location"
                      placeholder="City, State"
                      value={formData.pickup_location}
                      onChange={(e) => setFormData({ ...formData, pickup_location: e.target.value })}
                      required
                      className="h-12 pl-10 bg-secondary/30 border-white/10"
                      data-testid="pickup-location-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="delivery_location">Delivery Location</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-red-400" />
                    <Input
                      id="delivery_location"
                      placeholder="City, State"
                      value={formData.delivery_location}
                      onChange={(e) => setFormData({ ...formData, delivery_location: e.target.value })}
                      required
                      className="h-12 pl-10 bg-secondary/30 border-white/10"
                      data-testid="delivery-location-input"
                    />
                  </div>
                </div>
              </div>

              {/* Dates */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Pickup Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full h-12 justify-start text-left font-normal bg-secondary/30 border-white/10"
                        data-testid="pickup-date-btn"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {pickupDate ? format(pickupDate, 'PPP') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={pickupDate}
                        onSelect={setPickupDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label>Delivery Date (Optional)</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full h-12 justify-start text-left font-normal bg-secondary/30 border-white/10"
                        data-testid="delivery-date-btn"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {deliveryDate ? format(deliveryDate, 'PPP') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={deliveryDate}
                        onSelect={setDeliveryDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Price, Mileage & Count */}
              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="price">Price ($)</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-primary" />
                    <Input
                      id="price"
                      type="number"
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      required
                      className="h-12 pl-10 bg-secondary/30 border-white/10"
                      data-testid="price-input"
                    />
                  </div>
                  {formData.price && formData.mileage && parseFloat(formData.mileage) > 0 && (
                    <p className="text-xs text-[#4ADE80]">
                      ${(parseFloat(formData.price) / parseFloat(formData.mileage)).toFixed(2)}/mile
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="mileage">Total Miles</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="mileage"
                      type="number"
                      placeholder="e.g., 1200"
                      min="0"
                      value={formData.mileage}
                      onChange={(e) => setFormData({ ...formData, mileage: e.target.value })}
                      className="h-12 pl-10 bg-secondary/30 border-white/10"
                      data-testid="mileage-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="vehicle_count">Vehicle Count</Label>
                  <div className="relative">
                    <Package className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="vehicle_count"
                      type="number"
                      min="1"
                      value={formData.vehicle_count}
                      onChange={(e) => setFormData({ ...formData, vehicle_count: e.target.value })}
                      required
                      className="h-12 pl-10 bg-secondary/30 border-white/10"
                      data-testid="vehicle-count-input"
                    />
                  </div>
                </div>
              </div>

              {/* Vehicle Details (for auto transport) */}
              {formData.load_category === 'auto_transport' && (
                <div className="space-y-4 p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
                  <h3 className="font-semibold text-sm uppercase tracking-wider text-blue-400 flex items-center gap-2">
                    <Car className="w-4 h-4" />
                    Vehicle Details
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="vehicle_make_model">Make/Model</Label>
                      <Input
                        id="vehicle_make_model"
                        placeholder="e.g., Toyota Camry"
                        value={formData.vehicle_make_model}
                        onChange={(e) => setFormData({ ...formData, vehicle_make_model: e.target.value })}
                        className="bg-secondary/30 border-white/10"
                        data-testid="vehicle-make-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="vehicle_year">Year</Label>
                      <Input
                        id="vehicle_year"
                        placeholder="e.g., 2023"
                        value={formData.vehicle_year}
                        onChange={(e) => setFormData({ ...formData, vehicle_year: e.target.value })}
                        className="bg-secondary/30 border-white/10"
                        data-testid="vehicle-year-input"
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="is_running"
                        checked={formData.is_running}
                        onChange={(e) => setFormData({ ...formData, is_running: e.target.checked })}
                        className="rounded border-white/20 bg-secondary/30 text-blue-500 focus:ring-blue-500"
                      />
                      <Label htmlFor="is_running" className="cursor-pointer">Vehicle is running/operable</Label>
                    </div>
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="is_enclosed"
                        checked={formData.is_enclosed}
                        onChange={(e) => setFormData({ ...formData, is_enclosed: e.target.checked })}
                        className="rounded border-white/20 bg-secondary/30 text-blue-500 focus:ring-blue-500"
                      />
                      <Label htmlFor="is_enclosed" className="cursor-pointer">Requires enclosed transport</Label>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="vehicle_notes">Additional Notes</Label>
                    <Textarea
                      id="vehicle_notes"
                      placeholder="Any special requirements or notes about the vehicle(s)..."
                      value={formData.vehicle_notes}
                      onChange={(e) => setFormData({ ...formData, vehicle_notes: e.target.value })}
                      rows={3}
                      className="bg-secondary/30 border-white/10"
                      data-testid="vehicle-notes-input"
                    />
                  </div>
                </div>
              )}

              {/* Hotshot Details */}
              {formData.load_category === 'hotshot' && (
                <div className="space-y-4 p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                  <h3 className="font-semibold text-sm uppercase tracking-wider text-yellow-400 flex items-center gap-2">
                    <Zap className="w-4 h-4" />
                    Hotshot Details
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="weight">Weight (lbs)</Label>
                      <div className="relative">
                        <Scale className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="weight"
                          type="number"
                          placeholder="e.g., 5000"
                          value={formData.weight}
                          onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                          className="pl-10 bg-secondary/30 border-white/10"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Trailer Type</Label>
                      <Select
                        value={formData.trailer_type}
                        onValueChange={(value) => setFormData({ ...formData, trailer_type: value })}
                      >
                        <SelectTrigger className="bg-secondary/30 border-white/10">
                          <SelectValue placeholder="Select trailer type" />
                        </SelectTrigger>
                        <SelectContent>
                          {trailerTypes.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="dimensions_length">Length (ft)</Label>
                      <Input
                        id="dimensions_length"
                        type="number"
                        placeholder="Length"
                        value={formData.dimensions_length}
                        onChange={(e) => setFormData({ ...formData, dimensions_length: e.target.value })}
                        className="bg-secondary/30 border-white/10"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="dimensions_width">Width (ft)</Label>
                      <Input
                        id="dimensions_width"
                        type="number"
                        placeholder="Width"
                        value={formData.dimensions_width}
                        onChange={(e) => setFormData({ ...formData, dimensions_width: e.target.value })}
                        className="bg-secondary/30 border-white/10"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="dimensions_height">Height (ft)</Label>
                      <Input
                        id="dimensions_height"
                        type="number"
                        placeholder="Height"
                        value={formData.dimensions_height}
                        onChange={(e) => setFormData({ ...formData, dimensions_height: e.target.value })}
                        className="bg-secondary/30 border-white/10"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="is_time_sensitive"
                        checked={formData.is_time_sensitive}
                        onChange={(e) => setFormData({ ...formData, is_time_sensitive: e.target.checked })}
                        className="rounded border-white/20 bg-secondary/30 text-yellow-500 focus:ring-yellow-500"
                      />
                      <Label htmlFor="is_time_sensitive" className="cursor-pointer flex items-center gap-2">
                        <Zap className="w-4 h-4 text-yellow-400" />
                        Time-Sensitive / Expedited
                      </Label>
                    </div>
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="is_hazmat_hotshot"
                        checked={formData.is_hazmat}
                        onChange={(e) => setFormData({ ...formData, is_hazmat: e.target.checked })}
                        className="rounded border-white/20 bg-secondary/30 text-red-500 focus:ring-red-500"
                      />
                      <Label htmlFor="is_hazmat_hotshot" className="cursor-pointer flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-400" />
                        Hazmat
                      </Label>
                    </div>
                  </div>
                </div>
              )}

              {/* Freight Details */}
              {formData.load_category === 'freight' && (
                <div className="space-y-4 p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                  <h3 className="font-semibold text-sm uppercase tracking-wider text-green-400 flex items-center gap-2">
                    <Box className="w-4 h-4" />
                    Freight Details
                  </h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="weight_freight">Weight (lbs)</Label>
                      <div className="relative">
                        <Scale className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="weight_freight"
                          type="number"
                          placeholder="e.g., 20000"
                          value={formData.weight}
                          onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                          className="pl-10 bg-secondary/30 border-white/10"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Freight Class</Label>
                      <Select
                        value={formData.freight_class}
                        onValueChange={(value) => setFormData({ ...formData, freight_class: value })}
                      >
                        <SelectTrigger className="bg-secondary/30 border-white/10">
                          <SelectValue placeholder="Select class" />
                        </SelectTrigger>
                        <SelectContent>
                          {freightClasses.map((fc) => (
                            <SelectItem key={fc.value} value={fc.value}>
                              {fc.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="pallet_count">Pallet Count</Label>
                      <Input
                        id="pallet_count"
                        type="number"
                        placeholder="e.g., 24"
                        value={formData.pallet_count}
                        onChange={(e) => setFormData({ ...formData, pallet_count: e.target.value })}
                        className="bg-secondary/30 border-white/10"
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="dimensions_length_freight">Length (ft)</Label>
                      <Input
                        id="dimensions_length_freight"
                        type="number"
                        placeholder="Length"
                        value={formData.dimensions_length}
                        onChange={(e) => setFormData({ ...formData, dimensions_length: e.target.value })}
                        className="bg-secondary/30 border-white/10"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="dimensions_width_freight">Width (ft)</Label>
                      <Input
                        id="dimensions_width_freight"
                        type="number"
                        placeholder="Width"
                        value={formData.dimensions_width}
                        onChange={(e) => setFormData({ ...formData, dimensions_width: e.target.value })}
                        className="bg-secondary/30 border-white/10"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="dimensions_height_freight">Height (ft)</Label>
                      <Input
                        id="dimensions_height_freight"
                        type="number"
                        placeholder="Height"
                        value={formData.dimensions_height}
                        onChange={(e) => setFormData({ ...formData, dimensions_height: e.target.value })}
                        className="bg-secondary/30 border-white/10"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="is_ltl"
                        checked={formData.is_ltl}
                        onChange={(e) => setFormData({ ...formData, is_ltl: e.target.checked })}
                        className="rounded border-white/20 bg-secondary/30 text-green-500 focus:ring-green-500"
                      />
                      <Label htmlFor="is_ltl" className="cursor-pointer">LTL (Less Than Truckload)</Label>
                    </div>
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="is_hazmat_freight"
                        checked={formData.is_hazmat}
                        onChange={(e) => setFormData({ ...formData, is_hazmat: e.target.checked })}
                        className="rounded border-white/20 bg-secondary/30 text-red-500 focus:ring-red-500"
                      />
                      <Label htmlFor="is_hazmat_freight" className="cursor-pointer flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-400" />
                        Hazmat
                      </Label>
                    </div>
                  </div>
                </div>
              )}

              {/* Special Instructions (all categories) */}
              <div className="space-y-2">
                <Label htmlFor="special_instructions">Special Instructions (Optional)</Label>
                <Textarea
                  id="special_instructions"
                  placeholder="Any special requirements, handling instructions, or additional notes..."
                  value={formData.special_instructions}
                  onChange={(e) => setFormData({ ...formData, special_instructions: e.target.value })}
                  rows={3}
                  className="bg-secondary/30 border-white/10"
                />
              </div>

              {/* Submit */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(-1)}
                  className="flex-1 h-12"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 h-12 glow-gold uppercase tracking-wider font-semibold"
                  data-testid="post-load-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Posting...
                    </>
                  ) : (
                    'Post Load'
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PostLoadPage;
